import { SearchState } from '@/types/models'
import { isNil, isNonEmptyString } from '../../utils/checks'
import normalizeSearchState from './normalizeSearchState'
import { getSessionStorageSearchState } from '@/utils/search/searchState'

interface ClearRefinementFromSearchStateFunction {
  (taxonomy: string): Partial<SearchState>
}

/**
 * Clears the specified taxonomy from the refinement list in the search state.
 *
 * This function retrieves the current search state from session storage,
 * checks if the refinement list and taxonomy are valid, and then removes
 * the specified taxonomy from the refinement list. The updated search state
 * is then normalized and returned.
 *
 *
 * @example
 * // Assuming the current search state is:
 * // {
 * //   refinementList: {
 * //     taxonomy1: ['value1'],
 * //     taxonomy2: ['value2']
 * //   }
 * // }
 * const updatedSearchState = clearRefinementFromSearchState('taxonomy1');
 * // The updated search state will be:
 * // {
 * //   refinementList: {
 * //     taxonomy2: ['value2']
 * //   }
 * // }
 */
const clearRefinementFromSearchState: ClearRefinementFromSearchStateFunction = (
  taxonomy
) => {
  const searchState: SearchState = getSessionStorageSearchState() || {}

  if (isNil(searchState?.refinementList) || !isNonEmptyString(taxonomy)) {
    return searchState
  }

  const refinedSearchState: Partial<SearchState> = {
    ...searchState,
    refinementList: {
      ...Object.entries(searchState.refinementList) //make a array from the refinementList
        .filter((entry) => entry[0] !== taxonomy) //filter out the taxonomy
        //reduce the array back to an object
        .reduce((acc, entry) => {
          acc[entry[0]] = entry[1]
          return acc
        }, {})
    }
  }

  return normalizeSearchState(refinedSearchState)
}

export default clearRefinementFromSearchState
